import { useLocation } from "wouter";
import { Tabs, type TabItem } from "./Tabs";
import "./SectionSubNav.css";

/**
 * A section's sub-navigation, mounted above each of that section's own
 * top-level LIST pages (see `FleetSubNav`'s and `OperateSubNav`'s call
 * sites — App.tsx). Generalized out of the original Fleet-only
 * implementation (IA slice 2) once Operate needed the identical pattern
 * (A-12: a second copy earns the shared abstraction, not a hand-rolled
 * second tab bar).
 *
 * Each tab here is a full navigation to a sibling top-level route, not a
 * same-component tab-state change — unlike `ProjectDetailPage`/`GovernPage`/
 * `SettingsPage`, a section's pages are independent routes in App.tsx, not
 * `TabPanel`s of one container swapping content in place. `activeTab` is a
 * plain prop the mounting page passes in (it already knows unambiguously
 * which page it is) rather than re-derived from `useRoute`.
 *
 * No role gating here: whether a role can see the section at all is decided
 * once by `NavSidebar` (`canSeeFleet`, etc.); any narrower in-page mutation
 * gating is unchanged by this component (S-8, this is UX only — the server
 * enforces the real decision on every request).
 */
export function SectionSubNav<TabId extends string>({
  items,
  paths,
  activeTab,
  ariaLabel,
}: {
  items: TabItem[];
  paths: Record<TabId, string>;
  activeTab: TabId;
  ariaLabel: string;
}) {
  const [, navigate] = useLocation();

  return (
    <div className="section-subnav">
      <Tabs items={items} activeId={activeTab} onChange={(id) => navigate(paths[id as TabId])} ariaLabel={ariaLabel} />
    </div>
  );
}
